import { MutableRefObject, useCallback } from "react"
import { WebMercatorViewport } from "@deck.gl/core"
import { useAppDispatch } from "integrations/redux/hooks"
import { LngLatCoordinatePair, xyCoordinatePair } from "features/map/event/types"
import { PartialViewState } from "./types"
import { BoundingBox, footerHeight, navbarHeight, toolboxWidth } from "@nova/constants"
import { calculateIntersectionArea } from "@nova/calculate-geospatial"
import { getViewport } from "../GlobalViewState"
import { useResizableSidePanelWidth } from "panels/content/side-panel/SidePanel"

export const getMapDimensions = () => {
    const map = document.getElementById("map")
    return { width: map?.clientWidth || 800, height: map?.clientHeight || 800 }
}

export const useMapControl = () => {
    const dispatch = useAppDispatch()

    const clampViewstateDimensions = useCallback((viewState: PartialViewState) => {
        return {
            ...viewState,
            ...getMapDimensions(),
        }
    }, [])

    const unproject = useCallback((pixels: xyCoordinatePair) => {
        return getViewport().unproject(pixels)
    }, [])

    const project = useCallback((lngLat: number[]) => {
        return getViewport().project(lngLat)
    }, [])

    /**
     * project a coordinate to a screen pixel position, and clamp it to stay inside the screen boundaries
     * should be used for any menus we want to place on the map
     *
     * lngLat: the map coordinates of the element you want to project to the screen
     * viewState: the current view state of the map, should be provided directly from MapViewStateController
     * containerRef: a ref to the container you want to project to the screen
     * offset (vertical and horizontal): the amount of pixels you want to offset the element from the coordinate you provided, after it's been projected
     * alternateCoordinates: a backup spot to use in the case the primary spot goes off screen
     * alternateOffset: provide an offset to be used if the alternateCoordinate gets used
     */
    const projectAndClampToScreen = useCallback(
        (
            lngLat: [number, number],
            viewState: PartialViewState,
            containerRef: MutableRefObject<HTMLDivElement | null>,
            offsetVertical = 0,
            offsetHorizontal = 0,
            alternateCoordinate: [number, number],
            alternateOffset = 0
        ) => {
            //get the screen point position for both the primary coordinate and the alternate coordinate
            const viewport = getViewport()
            const xyCoordinates = viewport.project(lngLat) as [number, number]
            const alternatePoint = viewport.project(alternateCoordinate) as [number, number]

            const width = containerRef.current?.clientWidth || 0
            const height = containerRef.current?.clientHeight || 0

            //set up the boundaries for where we should clamp it
            const padding = 24
            let top = padding
            const left = padding + offsetHorizontal + 16
            const right = window.innerWidth - width - padding + offsetHorizontal - toolboxWidth
            let bottom = window.innerHeight - padding - footerHeight

            //check to see if we need to use the alternateCoordinate and the alternateOffset
            const offTheTop = xyCoordinates[1] < top + offsetVertical
            if (offTheTop) bottom = bottom + alternateOffset - height
            else top += offsetVertical

            //clamp the modal positions based on the boundaries calculated earlier
            const x = Math.max(Math.min(xyCoordinates[0], right), left)
            const y = Math.max(Math.min(xyCoordinates[1], bottom), top)
            const altX = Math.max(Math.min(alternatePoint[0], right), left)
            const altY = Math.max(Math.min(alternatePoint[1], bottom), top)

            return { projectedPixels: [x, y], alternatePixels: [altX, altY], altUsed: offTheTop }
        },
        []
    )

    //takes a coordinate on the map and returns all the sides of the screen it is off of, if any. Returns undefined if it fits on the screen
    const checkIfCoordinateIsOffscreen = useCallback(
        (viewState?: PartialViewState, coordinate?: number[]) => {
            if (!coordinate) return undefined
            const viewport = viewState
                ? new WebMercatorViewport(clampViewstateDimensions(viewState))
                : getViewport()
            const pixel = viewport.project(coordinate) as [number, number]
            const x = pixel[0]
            const y = pixel[1]

            const sides = []
            //off the top
            if (y < 0) sides.push("top")
            //off the right
            if (x > window.innerWidth) sides.push("right")
            //off the bottom
            if (y > window.innerHeight) sides.push("bottom")
            //off the left
            if (x < 0) sides.push("left")
            if (sides.length === 0) return undefined
            return sides
        },
        [clampViewstateDimensions]
    )

    const sidePanelWidth = useResizableSidePanelWidth()
    //checks if a polygon is offscreen by seeing if all of the provided points are off the same side of the screen
    const checkIfBoundingBoxIsOffscreen = useCallback(
        (boundingBox: BoundingBox, viewState: PartialViewState) => {
            try {
                const viewport = new WebMercatorViewport(clampViewstateDimensions(viewState))

                const topLeftPixel = viewport.project(boundingBox.topLeft) as LngLatCoordinatePair
                const bottomRightPixel = viewport.project(
                    boundingBox.bottomRight
                ) as LngLatCoordinatePair
                const topRightPixel = viewport.project(boundingBox.topRight) as LngLatCoordinatePair
                const bottomLeftPixel = viewport.project(
                    boundingBox.bottomLeft
                ) as LngLatCoordinatePair
                const pixelBoundingBox = {
                    topLeft: topLeftPixel,
                    bottomRight: bottomRightPixel,
                    topRight: topRightPixel,
                    bottomLeft: bottomLeftPixel,
                }
                const intersectArea = calculateIntersectionArea(pixelBoundingBox, [
                    window.innerWidth - sidePanelWidth,
                    window.innerHeight - navbarHeight - footerHeight,
                ])
                return intersectArea === 0
            } catch (err) {
                // viewport.project will throw on invalid coords
                console.error(err)
                return false
            }
        },
        [clampViewstateDimensions, sidePanelWidth]
    )

    //checks if a polygon is zoomed into by seeing if at least three points are all on different sides of the screen
    const checkIfPolygonIsZoomed = useCallback(
        (points: [number, number][], viewState: PartialViewState) => {
            const sides = ["top", "right", "bottom", "left"]
            const screenSides = points.map((point) =>
                checkIfCoordinateIsOffscreen(viewState, point)
            )
            if (screenSides.length === 0) return false

            let offscreenCount = 0
            sides.forEach((side) => {
                const polygonOff = screenSides.some((s) => s?.includes(side))
                if (polygonOff) offscreenCount++
            })
            return offscreenCount >= 3
        },
        [checkIfCoordinateIsOffscreen]
    )

    // Allow user to control the map view state
    return {
        project,
        unproject,
        dispatch,
        clampViewstateDimensions,
        projectAndClampToScreen,
        checkIfCoordinateIsOffscreen,
        checkIfBoundingBoxIsOffscreen,
        checkIfPolygonIsZoomed,
    }
}
